import React from "react";
import { Box, Container, Typography, Grid, Paper } from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SectionBackground from "./SectionBackground";

const About: React.FC = () => {
  const stats = [
    { value: "30+", label: "Years Combined Experience" },
    { value: "24/7", label: "Technical Support" },
    { value: "99.9%", label: "Uptime Guarantee" },
    { value: "100%", label: "US-Based Operations" },
  ];

  return (
    <Box
      id="about"
      sx={{
        py: { xs: 8, md: 12 },
        position: "relative",
        overflow: "hidden",
      }}
    >
      <SectionBackground variant="paper" />
      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography
              variant="h2"
              sx={{
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #ffffff 0%, #00a8ff 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              About Us
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: "text.secondary", mb: 4, lineHeight: 1.8 }}
            >
              Founded in 2023, ISS LLC is a US-based hosted solution provider
              serving both commercial and government customers.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", mb: 3, lineHeight: 1.8 }}
            >
              We offer a completely customizable experience for all your online
              hosting needs. With over 30 years of combined civilian and
              government service experience, we bring unparalleled expertise to
              every project.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", mb: 4, lineHeight: 1.8 }}
            >
              Our current service offerings include website server solutions and
              entertainment-based servers, all designed to be tailored to your
              individual needs no matter how unique.
            </Typography>

            <Grid container spacing={3}>
              {[
                {
                  icon: <BusinessIcon />,
                  title: "US-Based",
                  description: "Fully operated in the United States",
                },
                {
                  icon: <SupportAgentIcon />,
                  title: "24/7 Support",
                  description: "Round-the-clock technical assistance",
                },
                {
                  icon: <VerifiedUserIcon />,
                  title: "Trusted Partner",
                  description: "Commercial & government clients",
                },
              ].map((item, index) => (
                <Grid item xs={12} sm={4} key={index}>
                  <Box
                    sx={{
                      textAlign: "center",
                      p: 2,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 64,
                        height: 64,
                        borderRadius: "12px",
                        background:
                          "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
                        mb: 2,
                      }}
                    >
                      {React.cloneElement(item.icon, {
                        sx: { fontSize: 32, color: "white" },
                      })}
                    </Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {item.description}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              sx={{
                p: 4,
                bgcolor: "background.default",
                borderRadius: 3,
                border: "1px solid rgba(0, 168, 255, 0.2)",
              }}
            >
              <Typography
                variant="h4"
                sx={{ mb: 4, fontWeight: 700, textAlign: "center" }}
              >
                Let Us Manage Your Server
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: "text.secondary",
                  mb: 4,
                  textAlign: "center",
                  lineHeight: 1.8,
                }}
              >
                Already have a server or website deployed? Need a custom
                maintenance solution to keep your information up-to-date and
                relevant? Contact ISS LLC and we'll work with you to meet your
                needs.
              </Typography>

              <Grid container spacing={3}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        textAlign: "center",
                        bgcolor: "rgba(0, 168, 255, 0.1)",
                        border: "1px solid rgba(0, 168, 255, 0.3)",
                        borderRadius: 2,
                        transition: "all 0.3s ease",
                        "&:hover": {
                          transform: "scale(1.05)",
                          boxShadow: "0 8px 24px rgba(0, 168, 255, 0.2)",
                        },
                      }}
                    >
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 800,
                          background:
                            "linear-gradient(135deg, #00a8ff 0%, #0066cc 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          mb: 1,
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 500 }}
                      >
                        {stat.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;
